import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
	Button,
	Card,
	Grid,
	Step,
	StepLabel,
	Stepper,
	Typography,
	CircularProgress,
	Divider,
	Box,
	Stack,
} from '@mui/material';
import ClientDetailForm from '../ClientDetailForm';
import ProductQuestions from '../ProductQuestions';
import CoverageSummary from '../CoverageSummary/index';
import ReferQuoteModal from '../Modal';
import ConfirmModal from '../ConfirmModal';

import PolicyBoundView from '../PolicyBoundView';
import BindModal from '../BindCoverModal';

import { addMonths } from 'date-fns';
// import validationSchema from './validationSchema';
import { Form, Formik, useFormik } from 'formik';
import {
	updateQuote,
	updateQuoteClient,
	updateQuoteClientAndRatings,
	updateQuoteDecisionTree,
	updateQuotePolicyDates,
} from '../../helper/formatQuoteForUpdate';
import { useLocation, useNavigate } from 'react-router-dom';
import { createQuote } from '../../services/createQuote';
import QuoteSummaryProducts from '../QuoteSummaryProducts';
import { PolicyPeriodDatePickers } from '../PolicyPeriodDatePickers';
import {
	checkQuestionErrors,
	convertQuotePackageToPackage,
	convertQuoteProductToProduct,
	getDeclinedQuestions,
	getReferralQuestions,
	regionNames,
} from '../../helper/constants';
import { StatusNumber } from '../../helper/statusNumber';
import { MixpanelWrapper } from '../MixPanelWrapper';
import { CreateQuoteSummaryPanel } from '../CreateQuoteSummaryPanel';
import { useQuoteContext } from '../../shared/contexts/QuoteContext';
import {
	ApiSearchTypes,
	ClientEntityGroup,
	DecisionTree,
	DecisionTreeOrQuoteProduct,
	Package,
	Product,
} from '../../shared/types/quotes';
import {
	useCheckClientSanctionsMutation,
	useGetClientByABNQuery,
	useGetClientByACNQuery,
	useUpdateClientMutation,
} from '../../services/features/clientApi/clientApi';
import { useGetProductsByEntityCodeQuery } from '../../services/features/productApi/productApi';
import { colours } from '../../shared/colors';
import { RegionData } from '../../shared/types/regionData';
import {
	useUpdateQuoteGroupMutation,
	useCreateNewQuoteGroupMutation,
	useBindQuoteGroupMutation,
	useReferQuoteGroupMutation,
	useUpdateQuoteStatusToReferralApprovedModifiableMutation,
} from '../../services/features/quoteApi/quoteApi';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { clearError } from '../../services/features/errorSlice/errorSlice';
import {
	clearQuoteId,
	nextStage,
	prevStage,
	resetStage,
	setQuoteId,
	setStage,
} from '../../services/features/quoteSlice/quoteSlice';
import RemoveProductModal from '../RemoveProductModal';
import {
	closeAllDialogs,
	closeDialog,
	openDialog,
} from '../../services/features/dialogSlice/dialogSlice';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import ProductSelection from '../ProductSelection';
import useValidationSchema from './validationSchema';
import { ValidationError } from 'yup';
import UpdateQuoteStatusModal from '../UpdateQuoteStatusModal';

const QuoteStepper = () => {
	const { stages, stage } = useSelector((state: RootState) => state.quoteSlice);

	return (
		<Box pt={'1em'} pb={'2em'} className='createQuoteStepper_steps'>
			<Stepper activeStep={stage}>
				{stages.map((label) => (
					<Step key={label}>
						<StepLabel>{label}</StepLabel>
					</Step>
				))}
			</Stepper>
		</Box>
	);
};

interface BackButtonProps {
	disabled: boolean;
	handleConfirmOpen: () => void;
	handleExit: () => void;
	handleBack: () => void;
}

const BackButton = ({ disabled, handleBack, handleExit, handleConfirmOpen }: BackButtonProps) => {
	const { stage } = useSelector((state: RootState) => state.quoteSlice);

	return (
		<>
			{stage === 0 || stage > 3 ? (
				<Button
					variant='text'
					color='tertiary'
					onClick={stage > 3 ? handleExit : handleConfirmOpen}
				>
					Exit
				</Button>
			) : (
				<Button disabled={disabled} onClick={handleBack} variant='text'>
					Back
				</Button>
			)}
		</>
	);
};

interface InitialFormikState {
	businessNumber: any;
	acnBusinessNumber: any;
	companyName: any;
	occupation: any;
	suboccupationId: any;
	entityCode: any;
	entityGroup?: ClientEntityGroup | null;
	locked: any;
	streetName: any;
	city: any;
	region: any;
	postcode: any;
	startDate: Date | null;
	endDate: Date | null;
	totals: number[];
	products: Product[];
	packages: Package[];
}

/**
 * CreateQuoteForm component is used to handle the creation of quotes.
 * It uses a step-by-step process to guide the user through the quote creation.
 *
 * @returns {React.Element} Returns a React component.
 */
const CreateQuoteForm = () => {
	const location = useLocation();
	const navigate = useNavigate();

	const dispatch = useDispatch();

	const [updateQuoteGroup, { isLoading: isLoadingUpdate }] = useUpdateQuoteGroupMutation();
	const [updateClient, { isLoading: isLoadingUpdateClient }] = useUpdateClientMutation();
	const [createNewQuoteGroup, { isLoading: isLoadingNewQuote }] =
		useCreateNewQuoteGroupMutation();
	const [bindQuoteGroup] = useBindQuoteGroupMutation();
	const [updateQuoteStatus] = useUpdateQuoteStatusToReferralApprovedModifiableMutation();
	const [referQuoteGroup] = useReferQuoteGroupMutation();

	const [checkClientSanctions] = useCheckClientSanctionsMutation();

	const { quoteId, stages, stage, creatingQuote } = useSelector(
		(state: RootState) => state.quoteSlice
	);
	const errors = useSelector((state: RootState) => state.errorSlice);

	const initialValues: InitialFormikState = {
		businessNumber: '',
		acnBusinessNumber: '',
		companyName: '',
		occupation: '',
		suboccupationId: '',
		entityCode: '',
		locked: { products: [], packages: [] },
		streetName: '',
		city: '',
		region: 'QLD',
		postcode: '',
		startDate: null,
		endDate: null,
		totals: [0, 0, 0],
		products: [],
		packages: [],
	};

	const [loadedInitialState, setLoadedInitialState] = useState(false);

	const [removeProductClose, setRemoveProductClose] = useState<() => Promise<void>>(() =>
		Promise.resolve()
	);

	const { validationSchema, backdatedDateSchema } = useValidationSchema();
	const currentValidationSchema = validationSchema[stage];
	const isLastStep = stage === stages.length - 1;

	const [decisionTreeErrors, setDecisionTreeErrors] = useState<any>([]);

	const [needsProductRefresh, setNeedsProductRefresh] = useState(false);

	const {
		currentQuotes,
		quoteGroup,
		decisionTree,
		splitDecisionTree,
		ratingRegionsRows,
		isEveryQuoteInProgress,
		hasSanctions,
		needsReferral,
		isReferralApproved,
		isReferralApprovedModifiable,
		isQuoteInProgress,
		isDeclined,
		bindButtonEnabled,
		loadingProducts,
		setLoadingProducts,
		loading,
		setLoading,
		quoteCheckbox,
		setQuoteCheckbox,
	} = useQuoteContext();

	const readonly = !isQuoteInProgress && !isReferralApprovedModifiable;

	const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null);
	const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);

	const [referralQuestions, setReferralQuestions] = useState([] as any);
	const [declineQuestions, setDeclineQuestions] = useState([] as any);

	// Update address data and send it to the server
	const updateAddress = async () => {
		if (quoteGroup) {
			const region = formik.values.region;
			const newQuoteClient = {
				id: quoteGroup.quoteClient.id,
				// ...quoteGroup.quoteClient,
				stateCode: region,

				postcode:
					formik.values.postcode.length === 4
						? formik.values.postcode
						: quoteGroup.quoteClient.postcode,
				address1:
					formik.values.streetName.length > 0
						? formik.values.streetName
						: quoteGroup.quoteClient.address1,
				address2:
					formik.values.city.length > 0
						? formik.values.city
						: quoteGroup.quoteClient.address2,
			};
			await updateClient({ quoteId: quoteId ?? 0, clientData: newQuoteClient })
				.unwrap()
				.catch((error) => console.log('Error in updating address', error));
		}
	};

	/**
	 * Handles the "Next" button click event.
	 */
	const handleNext = async () => {
		if (isReferralApproved || !readonly) {
			setLoading(true);
		} else {
			dispatch(nextStage());
			window.scrollTo(0, 0);
			return;
		}

		switch (stage) {
			// If we are on the Client Details page, we check if this is a new quote or an existing quote
			// If it's an existing quote, we update some details
			case 0: {
				let quoteSuccess = true;
				if (!quoteId) {
					// we don't create the quote here any more
				} else {
					if (quoteGroup) {
						if (isReferralApproved) {
							await updateAddress();
						}
						const update = updateQuoteClient(quoteGroup, quoteClient);

						await updateQuoteGroup({ quote: update, currentQuote: quoteGroup })
							.unwrap()
							.then(() => (quoteSuccess = true))
							.catch((error) => {
								console.log('Error updating quote', error, errors);
								quoteSuccess = false;
							});
					}
				}
				if (quoteSuccess) {
					MixpanelWrapper.track('Create Quote - Client Details Filled', {
						Name: quoteGroup?.quoteClient?.clientHash,
						Occupation: formik.values.occupation,
						Employees: formik.values.totals[2],
						Turnover: formik.values.totals[0],
						Estimated: formik.values.totals[1],
					});
					dispatch(nextStage());
					window.scrollTo(0, 0);
				}
				break;
			}
			// If we are on the Select Products page, we check if any products or packages have been selected
			// We only proceed if there have been products or packages selected
			case 1: {
				if (formik.values.products.length > 0 || formik.values.packages.length > 0) {
					let quoteSuccess = false;
					if (quoteId == null) {
						quoteSuccess = await createNewQuote(ratingRegionsRows);
						console.time('wait');
						await new Promise((res) => setTimeout(res, 1500));
						console.timeEnd('wait');
					} else {
						if (quoteGroup) {
							const update = updateQuoteClientAndRatings(
								quoteGroup,
								quoteClient,
								ratingRegionsRows
							);
							await updateQuoteGroup({
								quote: update,
								currentQuote: quoteGroup,
							})
								.unwrap()
								.then(() => (quoteSuccess = true))
								.catch((error) => (quoteSuccess = false));
						}
					}
					if (quoteSuccess === true) {
						if (decisionTree.length === 0) {
							// Wait for 1500 ms to let decisionTree populate
							await new Promise((res) => setTimeout(res, 1500));
						}
						dispatch(nextStage());
					}
					window.scrollTo(0, 0);
				}
				break;
			}
			// If we are on the Product Questions page, we check if all the questions have been answered
			// We only proceed if all the questions have been answered
			case 2: {
				if (quoteGroup) {
					const update = updateQuoteDecisionTree(quoteGroup, decisionTree);
					await updateQuoteGroup({
						quote: update,
						currentQuote: quoteGroup,
					})
						.unwrap()
						.catch((error) => console.log('Error updating questions', error));

					const errors = handleDecisionTreeErrors();

					if (errors.length === 0) {
						dispatch(nextStage());
						// setActiveStep((prevActiveStep) => prevActiveStep + 1);
						window.scrollTo(0, 0);
					}
				}

				break;
			}
			// }
		}
		// If there are any referral questions; if so, we show the Referral Modal. If not, we show the Bind Quote Modal
		if (isLastStep) {
			MixpanelWrapper.track('Created Quote');
			if (needsReferral && !isReferralApproved) {
				setReferralQuestions(referralQuestions);
				handleOpenReferModal();
			} else {
				dispatch(openDialog('bindQuote'));
			}
		}
		// console.log('COMPLETE HANDLE NEXT', loading);
		setLoading(false);
	};

	const formik = useFormik({
		initialValues,
		validationSchema: currentValidationSchema,
		enableReinitialize: true,
		validateOnBlur: true,
		onSubmit: handleNext,
	});

	const acnEntityTypeCode =
		formik.values.entityGroup?.id === 1
			? 'APTY'
			: formik.values.entityGroup?.id === 2
			? 'APUB'
			: '';

	const businessNumber = formik.values.businessNumber;
	const acnBusinessNumber = formik.values.acnBusinessNumber;
	const searchType = formik.values?.entityGroup?.searchFlag;

	const clientAbnQuery = useGetClientByABNQuery(businessNumber ?? skipToken, {
		skip: !businessNumber || searchType !== ApiSearchTypes.ABN,
	});

	const clientAcnQuery = useGetClientByACNQuery(
		{ acn: acnBusinessNumber ?? skipToken, acnEntityTypeCode },
		{
			skip: !acnBusinessNumber || searchType !== ApiSearchTypes.ACN,
		}
	);

	const clientData = useMemo(
		() => clientAbnQuery.data ?? clientAcnQuery.data?.result ?? {},
		[clientAbnQuery.data, clientAcnQuery.data]
	);

	// useEffect(() => {
	// 	setLoading(clientAbnQuery.isFetching || clientAbnQuery.isLoading);
	// }, [clientAbnQuery.isFetching || clientAbnQuery.isLoading]);

	const quoteClient = useMemo(() => {
		const isACN = searchType === ApiSearchTypes.ACN;
		const isPrivateCompany = formik.values.entityGroup?.id === 1;
		const region = formik.values.region;
		let countryName;
		if (clientData?.address?.[0]?.iso3166CountryCode) {
			try {
				countryName = regionNames.of(clientData?.address?.[0]?.iso3166CountryCode);
			} catch (e) {
				console.log('Error getting country name from iso 3166 code', e);
			}
		}
		let clientAbnEntityCode = [
			quoteGroup?.quoteClient?.clientAbnEntityCode,
			clientData?.clientAbnEntityCode,
			clientData?.type,
			clientData?.abrEntity?.entityType,
			'',
		].find((v) => typeof v == 'string');

		// clientAbnEntityCode =
		// 	quoteGroup?.quoteClient?.clientAbnEntityCode ??
		// 	clientData?.clientAbnEntityCode ??
		// 	typeof clientData?.type === 'string'
		// 		? clientData?.type
		// 		: clientData?.abrEntity?.entityType ?? '';

		return {
			id: quoteGroup?.quoteClient?.id ?? 0,
			businessNumber:
				quoteGroup?.quoteClient?.businessNumber ?? searchType === ApiSearchTypes.ABN
					? formik.values.businessNumber
					: clientData?.abrEntity?.abn,
			acnBusinessNumber:
				quoteGroup?.quoteClient?.acnBusinessNumber ?? isACN
					? formik.values.acnBusinessNumber
					: null,
			organizationName:
				quoteGroup?.quoteClient?.organizationName ?? formik.values.companyName,
			description: quoteGroup?.quoteClient?.description ?? clientData?.typeDescription,
			website: quoteGroup?.quoteClient?.website ?? 'www',
			phone: quoteGroup?.quoteClient?.phone,
			email: quoteGroup?.quoteClient?.email,
			address1:
				formik.values.streetName.length > 0
					? formik.values.streetName
					: clientData?.addressLine1 ?? clientData?.address?.[0]?.addressLine,
			address2:
				formik.values.city.length > 0
					? formik.values.city
					: clientData?.addressLine2 ?? clientData?.address?.[0]?.locality,
			postcode:
				formik.values.postcode.length === 4
					? formik.values.postcode
					: clientData?.postcode ?? clientData?.address?.[0]?.postCode,
			stateCode: region,
			countryId: quoteGroup?.quoteClient?.countryId ?? 2,
			countryName:
				quoteGroup?.quoteClient?.countryName ?? clientData?.countryName ?? countryName,
			suboccupationId:
				quoteGroup?.quoteClient?.suboccupationId ?? formik.values.suboccupationId,
			suboccupationName:
				quoteGroup?.quoteClient?.suboccupationName ?? formik.values.occupation,
			clientEntityId: quoteGroup?.quoteClient?.clientEntityId, // ?? 9, // no longer hardcode as 9, retrieved in backend
			clientEntityName:
				quoteGroup?.quoteClient?.clientEntityName ?? clientData?.typeDescription ?? '',
			clientAbnEntityCode,
			// clientAbnEntityCode:
			// 	quoteGroup?.quoteClient?.clientAbnEntityCode ??
			// 	clientData?.clientAbnEntityCode ??
			// 	typeof clientData?.type === 'string'
			// 		? clientData?.type
			// 		: clientData?.abrEntity?.entityType ?? '',
			clientAcnEntityId:
				quoteGroup?.quoteClient?.clientAcnEntityId ??
				(isACN ? formik.values.entityGroup?.id : undefined),
			clientAcnEntityName:
				quoteGroup?.quoteClient?.clientAcnEntityName ??
				(isACN
					? isPrivateCompany
						? 'Australian Proprietary Company'
						: 'Australian Public Company'
					: undefined),
			clientAcnEntityCode:
				quoteGroup?.quoteClient?.clientAcnEntityCode ??
				(isACN ? (isPrivateCompany ? 'APTY' : 'APUB') : undefined),
		};
	}, [formik.values, quoteGroup, clientData, searchType]);

	const suboccupationId = parseInt(formik.values.suboccupationId);
	// useEffect(() => {
	//     console.log({
	// 		suboccupationId,
	// 		entityCode: formik.values.entityCode,
	// 	});
	// }, [suboccupationId, formik.values]);
	const productQuery = useGetProductsByEntityCodeQuery(
		{
			suboccupationId,
			entityCode: formik.values.entityCode,
		},
		{
			skip:
				!formik.values.suboccupationId ||
				isNaN(suboccupationId) ||
				(formik.values.entityCode ?? '').length === 0,
		}
	);

	const products: Product[] = useMemo(
		() => productQuery.data?.products ?? [],
		[productQuery.data?.products]
	);
	const packages: Package[] = useMemo(
		() => productQuery.data?.packages ?? [],
		[productQuery.data?.packages]
	);

	useEffect(() => {
		if (needsProductRefresh) {
			setLoading(true);
			updateSelections().then(() => {
				if (quoteGroup && formik.values.products.length === 0 && formik.values.packages.length === 0) {
					handleExit();
				}
				setNeedsProductRefresh(false);
				setLoading(false);
			});
		}
	}, [needsProductRefresh]);

	const resetQuoteValues = () => {
		if (quoteGroup) {
			const quotes = quoteGroup.quotes;

			const quoteClient = quoteGroup.quoteClient;
			const businessNumber = quoteClient.businessNumber ?? '';
			const acnBusinessNumber = quoteClient.acnBusinessNumber ?? '';

			const quoteProducts = quotes.flatMap((quote) => quote.quoteProducts);
			const quotePackages = quotes.flatMap((quote) => quote.quotePackages);
			const lockedQuotes = quotes.filter(
				(quote: any) => quote.status.statusNumber !== StatusNumber.QuoteInProgress
			);

			const selectedProducts = products.filter((p) =>
				quoteProducts.find((prod) => prod.id === p.id)
			);
			const selectedPackages = packages.filter((p) =>
				quotePackages.find((pack) => pack.id === p.id)
			);

			// const startDate = quotes[0].policyPeriodStartDate
			// 	? new Date(quotes[0].policyPeriodStartDate)
			// 	: initialValues.startDate;
			// const endDate = quotes[0].policyPeriodEndDate
			// 	? new Date(quotes[0].policyPeriodEndDate)
			// 	: initialValues.endDate;

			const employees = quotes[0].quoteRatingRegions.reduce((sum, region) => {
				return sum + region.staffNumbers;
			}, 0);
			const annualTurnover = quotes[0].quoteRatingRegions.reduce((sum, region) => {
				return sum + region.lastYearAmount;
			}, 0);
			const estimatedTurnover = quotes[0].quoteRatingRegions.reduce((sum, region) => {
				return sum + region.currentYearAmount;
			}, 0);

			formik.setValues({
				...formik.values,
				businessNumber,
				acnBusinessNumber,
				companyName: quoteClient.organizationName,
				occupation: quoteClient.suboccupationName,
				suboccupationId: quoteClient.suboccupationId,
				entityCode: quoteClient.clientAbnEntityCode ?? quoteClient.clientAcnEntityCode,
				locked: {
					products: lockedQuotes
						.flatMap((quote) => quote.quoteProducts)
						.map((product) => product.id),
					packages: lockedQuotes
						.flatMap((quote) => quote.quotePackages)
						.map((pack) => pack.id),
				},
				streetName: quoteClient.address1,
				city: quoteClient.address2,
				postcode: quoteClient.postcode,
				region: quoteClient.stateCode,
				// startDate,
				// endDate,
				totals: [annualTurnover, estimatedTurnover, employees],
				...((products.length > 0 || packages.length > 0) && {
					products: selectedProducts,
					packages: selectedPackages,
				}),
			});
			// setSelectedStartDate(startDate);
			// setSelectedEndDate(endDate);
			// dispatch(clearError());
		}
	};

	useEffect(() => {
		const updateData = async () => {
			if (quoteGroup) {
				setLoadingProducts(true);
				const update = updateQuotePolicyDates(
					quoteGroup,
					selectedStartDate,
					selectedEndDate
				);
				await updateQuoteGroup({ quote: update, currentQuote: quoteGroup })
					.unwrap()
					.catch((error) => {
						console.log('Error in updating dates', error);
						resetQuoteValues();
					});
				setLoadingProducts(false);
			}
		};
		if (quoteGroup && selectedStartDate != null && selectedEndDate != null) {
			// console.log('Update quote', { selectedStartDate, selectedEndDate, 'quoteGroup.quotes[0].policyPeriodStartDate': quoteGroup.quotes[0].policyPeriodStartDate, 'quoteGroup.quotes[0].policyPeriodEndDate': quoteGroup.quotes[0].policyPeriodEndDate })
			if (
				quoteGroup.quotes.length > 0 &&
				'policyPeriodStartDate' in quoteGroup.quotes[0] &&
				'policyPeriodEndDate' in quoteGroup.quotes[0]
			) {
				if (
					selectedStartDate?.valueOf() !=
						new Date(String(quoteGroup.quotes[0].policyPeriodStartDate)).valueOf() ||
					selectedEndDate?.valueOf() !=
						new Date(String(quoteGroup.quotes[0].policyPeriodEndDate)).valueOf()
				) {
					updateData();
				}
			}
		}
	}, [quoteGroup, selectedStartDate, selectedEndDate]);

	/**
	 * Handles the checkbox state changes for a given package and ID.
	 *
	 * If the specified ID and packageId are found in the current state, they are removed.
	 * Otherwise, they are added to the state.
	 *
	 * @param {any} id - The ID of the checkbox to be checked/unchecked.
	 * @param {any} packageId - The package ID associated with the checkbox.
	 * @returns {void}
	 */
	const checkBoxHandler = async (id: any, packId: any) => {
		// let updatedCheckbox = quoteCheckbox.slice();

		const packageId = packId === 'PRODUCT' ? packId : parseInt(packId);
		const check = quoteCheckbox.find((x: any) => x.packageId === packageId && x.id === id);
		console.log('Check', check);

		const updatedCheckbox = check
			? quoteCheckbox.slice().filter((x: any) => !(x.id === id && x.packageId === packageId))
			: [...quoteCheckbox.slice(), { packageId, id }];

		console.log('Updated quote checkbox', updatedCheckbox);
		const tempProducts = formik.values.products.filter(
			(p: Product) =>
				updatedCheckbox.find((x: any) => x.id === p.id && x.packageId === 'PRODUCT') == null
		);
		const tempPackages = formik.values.packages.map((packageItem: Package) => {
			let newProducts = (packageItem.products ?? []).filter(
				(p: any) =>
					updatedCheckbox.find(
						(x: any) => x.id === p.id && x.packageId === packageItem.id
					) == null
			);
			let newPackage;
			if ('quoteProducts' in packageItem) {
				newPackage = { ...packageItem, quoteProducts: newProducts };
			} else {
				newPackage = { ...packageItem, products: newProducts };
			}
			return newPackage;
		});
		if (
			quoteGroup &&
			(tempProducts.length < formik.values.products.length ||
				tempPackages.length < formik.values.packages.length)
		) {
			const newFunction = async () => {
				if (quoteGroup) {
					let update = await updateQuote(
						quoteGroup,
						packageId === 'PRODUCT' ? tempProducts : formik.values.products,
						packageId !== 'PRODUCT' ? tempPackages : formik.values.packages
					);
					update = updateQuoteDecisionTree(update, decisionTree);

					await updateQuoteGroup({ quote: update, currentQuote: quoteGroup })
						.unwrap()
						.catch((error) => console.log('Error in removing product', error));
				}
				packageId === 'PRODUCT'
					? formik.setFieldValue('products', tempProducts)
					: formik.setFieldValue('packages', tempPackages);
				setQuoteCheckbox(updatedCheckbox);
				setRemoveProductClose(() => Promise.resolve());
			};
			setRemoveProductClose(() => newFunction);
			const item =
				packageId === 'PRODUCT'
					? formik.values.products.find((r) => r.id !== id)
					: formik.values.packages.find((r) => r.id !== id);
			dispatch(openDialog({ key: 'removeProduct', params: { productName: item?.name } }));
		} else {
			if (quoteGroup) {
				let update = await updateQuote(quoteGroup, formik.values.products, tempPackages);
				update = updateQuoteDecisionTree(update, decisionTree);

				await updateQuoteGroup({ quote: update, currentQuote: quoteGroup })
					.unwrap()
					.catch((error) => console.log('Error in removing product', error));
			}
			packageId === 'PRODUCT'
				? formik.setFieldValue('products', tempProducts)
				: formik.setFieldValue('packages', tempPackages);
			setQuoteCheckbox(updatedCheckbox);
		}

		// if (tempProducts.length === 0 && tempPackages.length === 0) {
		// 	dispatch(openDialog('removeProduct'));
		// } else {
		// 	setQuoteCheckbox(updatedCheckbox);
		// 	// if (quoteGroup) {
		// 	// 	let update = await updateQuote(
		// 	// 		quoteGroup,
		// 	// 		tempProducts,
		// 	// 		tempPackages,
		// 	// 		formik.values,
		// 	// 		quoteClient,
		// 	// 		ratingRegionsRows
		// 	// 	);

		// 	// 	update = updateQuoteDecisionTree(update, decisionTree);

		// 	// 	await updateQuoteGroup({ quote: update, currentQuote: quoteGroup })
		// 	// 		.unwrap()
		// 	// 		.catch((error) =>
		// 	// 			console.log('Error updating quote from checkbox handler', error)
		// 	// 		);
		// 	// }
		// 	// setQuoteCheckbox((pre: any) => {
		// 	// 	const check = pre?.find((x: any) => x.packageId === packageId && x.id === id);
		// 	// 	if (check != null && check !== undefined) {
		// 	// 		return pre?.filter((x: any) => !(x.id === id && x.packageId === packageId));
		// 	// 	}
		// 	// 	return [...pre, { packageId: packageId, id: id }];
		// 	// });
		// }
	};

	/**
	 * Effect to handle the initialization of the quote creation process.
	 * This function tracks an event when a new quote is created. If a quote is already
	 * created or a specific state condition is met, it loads the existing quote. Otherwise,
	 * it clears relevant localStorage items and sets the initial state.
	 *
	 * @effect
	 */
	useEffect(() => {
		dispatch(clearError());
		dispatch(closeAllDialogs());
		MixpanelWrapper.track('Create Quote');
		if (quoteId == null) {
			initialiseNewQuote();
		}
	}, []);

	const initialiseNewQuote = () => {
		let startDate = new Date();
		let endDate = addMonths(startDate, 12);
		setSelectedStartDate(startDate);
		setSelectedEndDate(endDate);
		formik.setValues({
			...formik.values,
			startDate,
			endDate,
		});
		setLoadedInitialState(true);
	};

	/**
	 * Checks decision tree questions and initializes decision tree states.
	 * This function extracts the decision tree questions from the stored quote in localStorage,
	 * both general and product-specific. It then processes the extracted questions
	 * to identify any errors or discrepancies.
	 *
	 * @effect
	 * @listens decisionTree - Updates whenever `decisionTree` changes.
	 */
	useEffect(() => {
		checkDecisionTreeQuestions();
		let errors = [];
		let tempDecisionTree: DecisionTreeOrQuoteProduct[] = [];

		if (quoteGroup) {
			tempDecisionTree = [...tempDecisionTree, quoteGroup.decisionTreeGeneralQuestions];
			let quoteProducts = [
				...quoteGroup.quotes.flatMap((q) => q.quoteProducts),
				...quoteGroup.quotes.flatMap((q) =>
					q.quotePackages.flatMap((p) => p.quoteProducts)
				),
			];
			tempDecisionTree = [
				...tempDecisionTree,
				...quoteProducts.filter(
					(p) => (p.quoteProductDecisionTree?.decisionTreeQuestions ?? []).length > 0
				),
			];
		}
		for (let i = 0; i < tempDecisionTree.length; i++) {
			const currentTree = tempDecisionTree[i];
			const currentTab: any =
				'decisionTreeQuestions' in currentTree
					? currentTree
					: currentTree.quoteProductDecisionTree;
			const hasError = currentTab?.decisionTreeQuestions?.some((question: any) =>
				checkQuestionErrors(question)
			);
			if (hasError) {
				errors.push(i);
			}
		}
	}, [decisionTree]);

	const updateSelections = async () => {
		setLoadingProducts(true);
		try {
			await updateQuoteWithSelections();
		} catch (error) {
			console.log(error);
		} finally {
			setLoadingProducts(false);
		}
	};

	useEffect(() => {
		if (quoteGroup) {
			// (products.length > 0 || packages.length > 0)

			const quotes = quoteGroup.quotes;

			const quoteProducts = quotes.flatMap((quote) => quote.quoteProducts);
			const quotePackages = quotes.flatMap((quote) => quote.quotePackages);

			const selectedProducts =
				products.length > 0
					? products.filter((p) => quoteProducts.find((prod) => prod.id === p.id))
					: quoteProducts.map((p) => convertQuoteProductToProduct(p));
			const selectedPackages = (
				packages.length > 0
					? packages.filter((p) => quotePackages.find((pack) => pack.id === p.id))
					: quotePackages.map((p) => convertQuotePackageToPackage(p))
			).map((pack) => ({
				...pack,
				products: pack.products.filter((product) =>
					quotePackages
						.find((quotePackage) => quotePackage.id == pack.id)
						?.quoteProducts.map((quoteProduct) => quoteProduct.id)
						.includes(product.id)
				),
			}));

			if (JSON.stringify(formik.values.products) !== JSON.stringify(selectedProducts)) {
				console.log(
					'[useEffect] UPDATE FORMIK PRODUCTS',
					quoteGroup,
					products,
					selectedProducts
				);
				formik.setFieldValue('products', selectedProducts);
			}
			if (JSON.stringify(formik.values.packages) !== JSON.stringify(selectedPackages)) {
				console.log(
					'[useEffect] UPDATE FORMIK PACKAGES',
					quoteGroup,
					packages,
					selectedPackages
				);
				formik.setFieldValue('packages', selectedPackages);
			}
		}
	}, [quoteGroup, products, packages, formik.values.products, formik.values.packages]);

	useEffect(() => {
		if (quoteGroup) {
			const quotes = quoteGroup.quotes;

			const quoteClient = quoteGroup.quoteClient;
			const businessNumber =
				(quoteClient.businessNumber ?? '').length > 0 ? quoteClient.businessNumber : '';
			const acnBusinessNumber =
				(quoteClient.acnBusinessNumber ?? '').length > 0
					? quoteClient.acnBusinessNumber
					: '';

			const lockedQuotes = quotes.filter(
				(quote) => quote.status.statusNumber !== StatusNumber.QuoteInProgress
			);

			const startDate = quotes[0]?.policyPeriodStartDate
				? new Date(quotes[0].policyPeriodStartDate)
				: initialValues.startDate;
			const endDate = quotes[0]?.policyPeriodEndDate
				? new Date(quotes[0].policyPeriodEndDate)
				: initialValues.endDate;

			const quoteRegions = quotes[0]?.quoteRatingRegions ?? [];

			const employees = quoteRegions.reduce((sum, region) => {
				return sum + region.staffNumbers;
			}, 0);
			const annualTurnover = quoteRegions.reduce((sum, region) => {
				return sum + region.lastYearAmount;
			}, 0);
			const estimatedTurnover = quoteRegions.reduce((sum, region) => {
				return sum + region.currentYearAmount;
			}, 0);

			const newValues = {
				// ...formik.values,
				businessNumber,
				acnBusinessNumber,
				companyName: quoteClient.organizationName,
				occupation: quoteClient.suboccupationName,
				suboccupationId: quoteClient.suboccupationId,
				entityCode: quoteClient.clientAcnEntityCode ?? quoteClient.clientAbnEntityCode,
				locked: {
					products: lockedQuotes
						.flatMap((quote) => quote.quoteProducts)
						.map((product) => product.id),
					packages: lockedQuotes
						.flatMap((quote) => quote.quotePackages)
						.map((pack) => pack.id),
				},
				streetName: quoteClient.address1,
				city: quoteClient.address2,
				postcode: quoteClient.postcode,
				region: quoteClient.stateCode,
				startDate,
				endDate,
				totals: [annualTurnover, estimatedTurnover, employees],
			};

			Object.entries(newValues).forEach(([key, value]) => {
				if (
					JSON.stringify(formik.values[key as keyof InitialFormikState]) !==
					JSON.stringify(value)
				) {
					formik.setFieldValue(key, value);
				}
			});

			setSelectedStartDate((prev) => (startDate && startDate !== prev ? startDate : prev));
			setSelectedEndDate((prev) => (endDate && endDate !== prev ? endDate : prev));

			// Only set values if they are different from current values
			// if (JSON.stringify(formik.values) !== JSON.stringify(newValues)) {
			// 	console.log(
			// 		'[useEffect] UPDATE OF QUOTE GROUP',
			// 		quoteGroup,
			// 		loadedInitialState,
			// 		newValues
			// 	);
			// 	formik.setValues(newValues);
			// 	setSelectedStartDate(startDate);
			// 	setSelectedEndDate(endDate);
			// }

			if (quotes.some((q) => q.status.statusNumber === StatusNumber.QuotePolicyCreated)) {
				// Send user to Bind Quote page
				dispatch(setStage(stages.length));
				// setActiveStep(4);
			}

			if (!loadedInitialState) {
				setLoadedInitialState(true);
			}
		}
	}, [
		quoteGroup,
		loadedInitialState,
		initialValues.endDate,
		initialValues.startDate,
		stages.length,
	]);

	/**
	 * Creates a new quote based on the provided client information and product/package selections.
	 *
	 * This function does the following:
	 * 1. Filters the selected products based on the checked items.
	 * 2. Maps the selected packages and filters their products based on the checked items.
	 * 3. Sends a request to create a new quote using the modified product and package selections.
	 * 4. If successful, it updates the application state with the newly created quote details.
	 * 5. If unsuccessful, it triggers the error handling mechanism to provide feedback to the user.
	 *
	 * Assumptions:
	 * - `selectedProducts` and `selectedPackages` are arrays in the current context that hold selected
	 *   products and packages respectively.
	 * - `quoteCheckbox` is an array that contains checked items.
	 * - The `createQuote` function sends a request to an external system or API to create a quote.
	 * - The structure of the response from `createQuote` is expected to have an `id` property when successful.
	 *   manage the application state.
	 *
	 * @param {any} values - Additional information required for the quote creation.
	 * @returns {boolean} - Returns true if the quote creation was successful, otherwise false.
	 */
	const createNewQuote = async (ratingRegionsRows: RegionData[]) => {
		try {
			// let tempProducts = selectedProducts.filter(
			let tempProducts = formik.values.products.filter(
				(p: any) =>
					quoteCheckbox.find((x: any) => x.id === p.id && x.packageId === 'PRODUCT') ==
					null
			);
			// let tempPackages = selectedPackages.map((packageItem: any) => {
			let tempPackages = formik.values.packages.map((packageItem: any) => {
				let newProducts = (packageItem.quoteProducts ?? packageItem.products ?? []).filter(
					(p: any) =>
						quoteCheckbox.find(
							(x: any) => x.id === p.id && x.packageId === packageItem.id
						) == null
				);
				let newPackage;
				if ('quoteProducts' in packageItem) {
					newPackage = { ...packageItem, quoteProducts: newProducts };
				} else {
					newPackage = { ...packageItem, products: newProducts };
				}
				return newPackage;
			});

			const newQuote = await createQuote(
				quoteClient,
				tempProducts,
				tempPackages,
				formik.values,
				ratingRegionsRows
			);
			return await createNewQuoteGroup(newQuote)
				.unwrap()
				.then(async (res) => {
					dispatch(setQuoteId(res.id));
					await checkSanctions();
					return true;
				});
		} catch (error: any) {
			console.log('🚀 ~ error:', error);
			// setLoading(false);
		}
		return false;
	};

	const getClientName = () => {
		const organisationName =
			'organizationName' in clientData ? clientData?.organizationName : null;
		const fullName = 'fullName' in clientData ? clientData?.fullName : null;
		const givenName =
			'givenName' in clientData && 'familyName' in clientData && clientData.givenName
				? `${clientData?.givenName} ${clientData?.familyName}`
				: null;
		const nameValue = 'name' in clientData ? clientData?.name?.nameValue : null;

		// console.log('GET CLIENT NAME', { organisationName, fullName, givenName, nameValue })
		const newName = organisationName ?? fullName ?? givenName ?? nameValue ?? '';
		return newName;
	};

	const checkSanctions = async () => {
		// const name =
		// 	clientData.organizationName ??
		// 	clientData.fullName ??
		// 	(clientData.givenName != null && clientData.familyName != null
		// 		? `${clientData.givenName} ${clientData.familyName}`
		// 		: '');
		const name = getClientName();
		await checkClientSanctions({
			businessNumber: clientData.businessNumber ?? clientData.identifier?.number,
			name,
			type: typeof clientData.type === 'object' ? clientData.type?.code : clientData.type,
		})
			.unwrap()
			.then(async (res) => {
				console.log('Sanctions res', res);
				if (res) {
					console.log('Trigger referral', res);
					// Needs referral
					// await handleReferral({
					// 	files: [],
					// 	reason: "Possible sanctions",
					// 	comments: `Possible sanctions match with search record id of ${res}`,
					// });
				}
			})
			.catch((error: any) => {
				console.log('Error performing sanctions search', error);
			});
	};
	/**
	 * Updates an existing quote with new client details and ratings.
	 *
	 * This function retrieves the selected quote from local storage and attempts to update it
	 * with the provided quote client information and values using the `updateQuoteClientAndRatings` function.
	 * If the update is successful, the function sets the error state to false, updates the error message,
	 * and then updates the local storage with the ID and details of the updated quote.
	 * It also updates the current quotes state.
	 *
	 * Assumptions:
	 * - The selected quote stored in local storage is identified by the key "selectedQuote".
	 * - The `updateQuoteClientAndRatings` function is used to update the quote client details and ratings for a given quote.
	 * - The `generateGenericError` function handles any errors that may arise during the quote update.
	 *
	 * @param {any} values - The updated ratings values for the quote.
	 * @returns {Promise<boolean>} Returns true if the update was successful, otherwise false.
	 */
	const updateExistingQuote = async () => {
		if (quoteGroup) {
			const update = updateQuoteClientAndRatings(
				quoteGroup,
				quoteClient,
				formik.values,
				ratingRegionsRows
			);
			await updateQuoteGroup({
				quote: update,
				currentQuote: quoteGroup,
			})
				.unwrap()
				.catch((error) => console.log('Error in updating quote', error));
		}
	};

	/**
	 * Removes a specific package from the list of selected packages and updates the quote accordingly.
	 *
	 * This function updates the array of selected packages by removing the package with the specified package ID.
	 * It then retrieves the quote ID from local storage and calls the `updateQuote` function with the modified list of packages.
	 * After the quote is updated, it further calls the `updateQuoteGroupQuotes` function.
	 *
	 * Assumptions:
	 * - The quote stored in local storage is identified by its "quoteId".
	 * - The `updateQuote` and `updateQuoteGroupQuotes` functions handle asynchronous operations related to updating the quote.
	 *
	 * @param {number} packageId - The ID of the package to be removed.
	 * @returns {Promise<void>} Resolves when the package removal and quote update operations are complete.
	 */
	const removePackage = async (packageId: number | string) => {
		const id = parseInt(packageId.toString());
		const newPackages = [...formik.values.packages]
			.slice()
			.filter((item: Package) => item.id !== id);
		if (quoteGroup) {
			const newFunction = async () => {
				setLoadingProducts(true);
				formik.setFieldValue('packages', newPackages);
				if (quoteGroup) {
					const update = await updateQuote(
						quoteGroup,
						formik.values.products,
						newPackages
					);
					await updateQuoteGroup({ quote: update, currentQuote: quoteGroup })
						.unwrap()
						.catch((error) => console.log('Error in removing package', error));
				}
				setLoadingProducts(false);
				if (newPackages.length === 0 && formik.values.packages.length === 0) {
					handleExit();
				}
				setRemoveProductClose(() => Promise.resolve());
			};
			setRemoveProductClose(() => newFunction);
			const removedPackage = formik.values.packages.find((item) => item.id === id);

			dispatch(
				openDialog({
					key: 'removeProduct',
					params: {
						productName: removedPackage?.name,
					},
				})
			);
		} else {
			formik.setFieldValue('packages', newPackages);
		}

		// if (newPackages.length === 0 && formik.values.packages.length === 0) {
		// 	dispatch(openDialog('removeProduct'));
		// } else {
		// 	setLoadingProducts(true);

		// 	formik.setFieldValue('packages', newPackages);
		// 	if (quoteGroup) {
		// 		const update = await updateQuote(quoteGroup, formik.values.products, newPackages);
		// 		await updateQuoteGroup({ quote: update, currentQuote: quoteGroup })
		// 			.unwrap()
		// 			.catch((error) => console.log('Error in removing package', error));
		// 	}
		// 	setLoadingProducts(false);
		// }
	};

	/**
	 * Removes a specific product from the list of selected products and updates the quote accordingly.
	 *
	 * This function updates the array of selected products by removing the product with the specified product ID.
	 * It then retrieves the quote ID from local storage and calls the `updateQuote` function with the modified list of products.
	 * After the quote is updated, it further calls the `updateQuoteGroupQuotes` function.
	 *
	 * Assumptions:
	 * - The quote stored in local storage is identified by its "quoteId".
	 * - The `updateQuote` and `updateQuoteGroupQuotes` functions handle asynchronous operations related to updating the quote.
	 *
	 * @param {number} productId - The ID of the product to be removed.
	 * @returns {Promise<void>} Resolves when the product removal and quote update operations are complete.
	 */
	const removeProduct = async (productId: number) => {
		const newProducts = [...formik.values.products].filter(
			(item: Product) => item.id !== productId
		);
		if (quoteGroup) {
			const newFunction = async () => {
				setLoadingProducts(true);
				if (quoteGroup) {
					const update = await updateQuote(
						quoteGroup,
						newProducts,
						formik.values.packages
					);
					await updateQuoteGroup({ quote: update, currentQuote: quoteGroup })
						.unwrap()
						.catch((error) => console.log('Error in removing product', error));
				}
				formik.setFieldValue('products', newProducts);
				setLoadingProducts(false);
				if (newProducts.length === 0 && formik.values.packages.length === 0) {
					handleExit();
				}
				setRemoveProductClose(() => Promise.resolve());
			};
			setRemoveProductClose(() => newFunction);
			const removedProduct = formik.values.products.find((item) => item.id === productId);
			dispatch(
				openDialog({
					key: 'removeProduct',
					params: {
						productName: removedProduct?.name,
					},
				})
			);
		} else {
			formik.setFieldValue('products', newProducts);
		}
		// if (newProducts.length === 0 && formik.values.packages.length === 0) {
		// 	dispatch(openDialog('removeProduct'));
		// } else {
		// 	setLoadingProducts(true);
		// 	if (quoteGroup) {
		// 		const update = await updateQuote(quoteGroup, newProducts, formik.values.packages);
		// 		await updateQuoteGroup({ quote: update, currentQuote: quoteGroup })
		// 			.unwrap()
		// 			.catch((error) => console.log('Error removing product', error));
		// 	}
		// 	setLoadingProducts(false);
		// }
	};

	const handleAbandonQuote = async () => {
		setLoading(true);
		if (quoteGroup) {
			const update = await updateQuote(quoteGroup, [], []);
			await updateQuoteGroup({ quote: update, currentQuote: quoteGroup })
				.unwrap()
				.catch((error) => console.log('Error removing products/packages', error));
		}
		handleExit();
	};

	/**
	 * Updates the selected quote with product and package selections.
	 *
	 * This function retrieves the quote ID from local storage, then processes the selected products
	 * and packages, ensuring that those already present in the quoteCheckbox array are excluded.
	 * The modified products and packages arrays are then used to call the `updateQuote` function.
	 * If the update is successful, it clears any generic error and further calls the
	 * `updateQuoteGroupQuotes` function.
	 *
	 * Assumptions:
	 * - The quote stored in local storage is identified by its "quoteId".
	 * - The `quoteCheckbox` array holds entries of products and packages that are already accounted for in the quote.
	 * - The `updateQuote` and `updateQuoteGroupQuotes` functions handle asynchronous operations related to updating the quote.
	 *
	 * @returns {Promise<boolean>} Returns true if the update is successful, false otherwise.
	 */
	const updateQuoteWithSelections = async () => {
		const tempProducts = formik.values.products.filter(
			// let tempProducts = selectedProducts.filter(
			(p) =>
				quoteCheckbox.find((x: any) => x.id === p.id && x.packageId === 'PRODUCT') == null
		);
		const tempPackages = formik.values.packages.map((packageItem) => {
			// let tempPackages = selectedPackages.map((packageItem: any) => {
			let newProducts = (packageItem.products ?? []).filter(
				(p) =>
					quoteCheckbox.find(
						(x: any) => x.id === p.id && x.packageId === packageItem.id
					) == null
			);
			let newPackage;
			if ('quoteProducts' in packageItem) {
				newPackage = { ...packageItem, quoteProducts: newProducts };
			} else {
				newPackage = { ...packageItem, products: newProducts };
			}
			return newPackage;
		});

		if (quoteGroup && quoteGroup.quotes.length > 0) {
			let update = await updateQuote(
				quoteGroup,
				tempProducts,
				tempPackages,
				formik.values,
				quoteClient,
				ratingRegionsRows
			);

			update = updateQuoteDecisionTree(update, decisionTree);

			await updateQuoteGroup({ quote: update, currentQuote: quoteGroup })
				.unwrap()
				.catch((error) => console.log('Error in updating decision tree', error));
		}
	};

	// useEffect(() => {
	//     // console.log('FORMIK', currentQuotes, products, packages, formik.setFieldValue);
	// 	if (currentQuotes.length > 0) {
	//         console.log(products, packages, formik.values.products, formik.values.packages);
	// 		const quoteProducts = currentQuotes.flatMap((quote) => quote.quoteProducts);
	// 		const quotePackages = currentQuotes.flatMap((quote) => quote.quotePackages);

	// 		const selectedProducts =
	// 			products.length > 0
	// 				? products.filter((p) => quoteProducts.find((prod) => prod.id === p.id))
	// 				: quoteProducts.map((p) => convertQuoteProductToProduct(p));
	// 		const selectedPackages =
	// 			packages.length > 0
	// 				? packages.filter((p) => quotePackages.find((pack) => pack.id === p.id))
	// 				: quotePackages.map((p) => convertQuotePackageToPackage(p));

	//         console.log('SELECTED PACKAGES', selectedPackages, 'SELECTED PRODUCTS', selectedProducts);
	// 		formik.setFieldValue('packages', selectedPackages);
	// 		formik.setFieldValue('products', selectedProducts);
	// 	}
	// }, [currentQuotes, products, packages, formik.values.products, formik.values.packages]);

	/**
	 * Analyzes the decision tree questions to determine various question categories.
	 *
	 * The function identifies questions that lead to different actions such as referral, decline,
	 * or additional questions. It then accumulates these questions in their respective temporary
	 * arrays. After processing all the decision tree questions, it updates the related state values.
	 *
	 * - Referral Questions: Questions that have a selected option leading to a 'REFER' action.
	 * - Decline Questions: Questions that have a selected option leading to a 'DECLINE' action.
	 * - Additional Questions: Questions that have a selected option leading to an 'ADDITIONAL QUESTION' action.
	 *
	 * Note: Any question can have multiple options, but only the option with the attribute 'selected' is considered.
	 *
	 * @returns {void}
	 */
	const checkDecisionTreeQuestions = () => {
		const { tempReferral, tempDecline } = decisionTree.reduce(
			(acc: any, tree: DecisionTreeOrQuoteProduct) => {
				const currentTab =
					'decisionTreeQuestions' in tree ? tree : tree?.quoteProductDecisionTree;
				const newReferral = getReferralQuestions(currentTab);
				const newDecline = getDeclinedQuestions(currentTab);

				acc.tempReferral = acc.tempReferral.concat(newReferral);
				acc.tempDecline = acc.tempDecline.concat(newDecline);

				return acc;
			},
			{ tempReferral: [], tempDecline: [] }
		);

		setDeclineQuestions(tempDecline);
		setReferralQuestions(tempReferral);
		// setShowReferMessage(tempReferral.length > 0);
	};

	/**
	 * Analyzes the decision tree for any errors and updates the decision tree errors state.
	 * This function goes through each item in the decision tree, checks if there's an error,
	 * and adds the index of that item to the errors array. If the 'showGeneralStatementOfFact' flag is set,
	 * the string "lastPanel" is added to the errors array.
	 *
	 * @async
	 */
	const handleDecisionTreeErrors = () => {
		// let errors = [];
		const errors = splitDecisionTree
			.map((tree, i) => {
				const currentTab = (
					'decisionTreeQuestions' in tree ? tree : tree.quoteProductDecisionTree
				) as DecisionTree;
				const hasError = currentTab?.decisionTreeQuestions?.some((question) =>
					checkQuestionErrors(question)
				);
				return hasError ? i : -1;
			})
			.filter((i) => i > -1);
		// console.log('ERRORS', errors);
		setDecisionTreeErrors(errors);
		return errors;
	};

	const handleConfirmOpen = () => dispatch(openDialog('confirm'));
	const handleUpdateQuoteOpen = () => dispatch(openDialog('updateQuote'));

	const handleExit = () => {
		// Exit the Create Quote screen
		dispatch(closeDialog('confirm'));
		dispatch(closeDialog('updateQuote'));
		dispatch(clearQuoteId());
		dispatch(resetStage());
		dispatch(clearError());
		navigate('/');
	};

	const handleUpdateQuoteStatus = async () => {
		if (quoteGroup) {
			setLoading(true);
			await updateQuoteStatus(quoteGroup.id)
				.unwrap()
				.then((res) => {
					console.log('Updated quote status', res);
					// dispatch(nextStage());
				})
				.catch((error) =>
					console.log(
						'Error updating quote group status to referral approved modifiable',
						error
					)
				);
			dispatch(closeDialog('updateQuote'));
			setLoading(false);
		}
	};

	const handleSaveAndExit = async () => {
		if (isReferralApproved || !readonly) {
			setLoading(true);
		} else {
			handleExit();
			return;
		}
		// const clientData = clientAbnQuery.data;
		switch (stage) {
			// If we are on the Client Details page, we update some details
			case 0: {
				await updateExistingQuote();
				break;
			}
			// If we are on the Select Products page, we update quote if any products or packages have been selected
			case 1: {
				if (!formik.errors.products && !formik.errors.packages) {
					// if (selectedProducts.length > 0 || selectedPackages.length > 0) {
					// if (values.products.length > 0 || values.packages.length > 0) {
					try {
						if (quoteId == null) {
							await createNewQuote(ratingRegionsRows);
						} else {
							await updateQuoteWithSelections();
						}
					} catch (error: any) {
						console.log('🚀 ~ error:', error);
					}
				}
				break;
			}
			// If we are on the Product Questions page, we submit the questions that have been answered
			case 2: {
				try {
					// if (quoteGroup) {
					// 	const update = updateQuoteDecisionTree(quoteGroup, decisionTree);

					// 	await updateQuoteGroup({
					// 		quote: update,
					// 		currentQuote: quoteGroup,
					// 	}).unwrap()
					//     .catch((error) => console.log('Error updating decision tree', error));
					// }
					await updateQuoteWithSelections();
				} catch (error: any) {
					console.log('🚀 ~ error:', error);
				}
				break;
			}
		}
		setLoading(false);
		handleExit();
	};

	const handleBack = () => {
		// Reset generic error messaging
		dispatch(clearError());
		// Return to previous page and scroll window to top
		dispatch(prevStage());
		// setActiveStep(activeStep - 1);
		window.scrollTo(0, 0);
	};

	const handleOpenReferModal = async () => {
		// if (options != null) {
		formik.validateForm().then((values: any) => {
			formik.setTouched({
				...formik.touched,
				streetName: true,
				city: true,
				postcode: true,
			});
			if (Object.keys(values).length === 0) {
				dispatch(openDialog('referQuote'));
			} else {
				window.scrollTo(0, 0);
			}
		});
	};

	/**
	 * Handles the referral of a quote based on the given values.
	 * The function retrieves a quote ID from local storage, then attempts to refer the quote using the provided values.
	 * If the returned data contains an ID, any generic error is cleared. Otherwise, a generic error is generated.
	 * Additionally, the status of the 'openReferModal' is toggled after the referral attempt.
	 *
	 * @async
	 * @param {object} values - An object containing details for the referral.
	 * @param {FileList} values.files - The files associated with the referral.
	 * @param {string} values.reason - The reason for the referral.
	 * @param {string} values.comments - Any additional comments related to the referral.
	 * @throws Will log an error message if the referral process encounters an error.
	 */
	const handleReferral = async (values: { files: any[]; reason: string; comments: string }) => {
		if (quoteGroup) {
			// console.log("REFERRAL VALUES", values, "QUOTEGROUP", quoteGroup);
			// Check here for sanctions and add that to the referral
			const referralReason = `${values.reason} ${
				hasSanctions ? 'Possible sanctions' : ''
			}`.trim();
			const comments = `${values.comments} ${
				hasSanctions
					? `Possible sanctions match with search record id of ${quoteGroup.quoteClientSanctions?.searchId}. Search reference ${quoteGroup.quoteClientSanctions?.searchReference}`
					: ''
			}`;

			await referQuoteGroup({
				quote: quoteGroup,
				files: values.files,
				referralReason,
				comments,
			})
				.unwrap()
				.then((res) => console.log('Quote sent for referral', res))
				.catch((error) => console.log('Error sending quote for referral'));
		}
		dispatch(closeDialog('referQuote'));
	};

	/**
	 * Handles the process of binding a policy.
	 * The function attempts to bind a policy and logs the returned data.
	 * If the returned data contains an ID, any generic error is cleared and the active step is incremented.
	 * Otherwise, a generic error is generated.
	 * Additionally, the loading state and the 'bindOpen' state are updated appropriately during the function's execution.
	 *
	 * @async
	 * @throws Will suppress any error encountered during the bind process and logs it.
	 */
	const handleBindQuote = async () => {
		if (quoteGroup) {
			setLoading(true);
			await bindQuoteGroup(quoteGroup.id)
				.unwrap()
				.then((res) => {
					dispatch(nextStage());
				})
				.catch((error) => console.log('Error binding quote', error));
			dispatch(closeDialog('bindQuote'));
			setLoading(false);
		}
	};

	const stepContent = [
		<ClientDetailForm options={formik} />,
		<ProductSelection
			options={formik}
			setNeedsProductRefresh={setNeedsProductRefresh}
			setRemoveProductClose={setRemoveProductClose}
			// quoteCheckbox={quoteCheckbox}
		/>,
		<ProductQuestions
			options={formik}
			decisionTreeErrors={decisionTreeErrors}
			// quoteCheckbox={quoteCheckbox}
			referralQuestions={referralQuestions}
			setReferralQuestions={setReferralQuestions}
			declineQuestions={declineQuestions}
			setDeclineQuestions={setDeclineQuestions}
		/>,
		<CoverageSummary
			options={formik}
			removePackage={removePackage}
			removeProduct={removeProduct}
			referralQuestions={referralQuestions}
			// quoteCheckbox={quoteCheckbox}
			checkBoxHandler={checkBoxHandler}
		/>,
		<PolicyBoundView />,
	];

	const submitButtonDisabled =
		(stage === 1 && products.length === 0 && packages.length === 0) ||
		(stage === 2 && declineQuestions.length > 0) ||
		(isLastStep && !bindButtonEnabled);

	const bindButtonVisible = isLastStep && !isDeclined && (isReferralApprovedModifiable || (isQuoteInProgress && !needsReferral) || isReferralApproved);

	const allLoading = useMemo(
		() => loading || loadingProducts || isLoadingUpdate || isLoadingNewQuote,
		[loading, loadingProducts, isLoadingUpdate, isLoadingNewQuote]
	);

	const handleSubmit = () => {
		if (!quoteGroup) {
			formik.handleSubmit();
		} else {
			formik.validateForm().then(async (errors) => {
				console.log('ERRORS VALIDATING', errors);
				if (Object.keys(errors).length === 1 && errors.startDate) {
					await backdatedDateSchema
						.validate(formik.values)
						.catch((err: ValidationError) => {
							console.log('Error validating start date', err.message);
							formik.setFieldError('startDate', err.message);
							return;
						});
				}
				await handleNext();
			});
		}
	};

	return (
		<div className='createQuoteStepper_container'>
			<QuoteStepper />
			<div className='createQuoteStepper_content'>
				{loadedInitialState && (
					<form className='createQuoteStepper_withCard'>
						<Grid container spacing={4}>
							<Grid
								item
								sm={12}
								md={true}
								sx={{
									position: 'sticky',
								}}
							>
								<Card
									elevation={0}
									sx={{
										borderRadius: '7px',
										mb: '4em',
									}}
								>
									{stepContent[stage]}
									<Stack
										p='1em'
										direction='row'
										alignItems='center'
										justifyContent='space-between'
									>
										<BackButton
											handleBack={handleBack}
											handleConfirmOpen={handleConfirmOpen}
											handleExit={handleExit}
											disabled={allLoading}
										/>
										<Stack
											direction='row'
											alignItems='center'
											justifyContent='end'
											spacing={'0.5em'}
										>
											{stage < 4 &&
												isReferralApproved &&
												!creatingQuote &&
												quoteGroup !== undefined && (
													<Button
														disabled={allLoading}
														variant='contained'
														color='secondary'
														onClick={handleUpdateQuoteOpen}
													>
														{'Edit Quote'}
													</Button>
												)}
											{stage < 4 &&
												!creatingQuote &&
												quoteGroup !== undefined && (
													<Button
														disabled={allLoading || isDeclined}
														variant='contained'
														color='secondary'
														onClick={handleSaveAndExit}
													>
														{'Save & Exit'}
													</Button>
												)}
											{stage === 3 && !isReferralApproved && (
												<Button
													disabled={(!isEveryQuoteInProgress && !isReferralApprovedModifiable) || allLoading}
													variant='contained'
													color='secondary'
													onClick={handleOpenReferModal}
												>
													Refer client
												</Button>
											)}
											{(stage < 3 || bindButtonVisible) && (
												<Button
													variant='containedTertiary'
													disabled={submitButtonDisabled || allLoading}
													onClick={handleSubmit}
												>
													{allLoading ? (
														<CircularProgress
															size={'1.5em'}
															sx={{
																color: 'white',
															}}
														/>
													) : isLastStep ? (
														'Bind Cover'
													) : (
														'Next'
													)}
												</Button>
											)}
										</Stack>
									</Stack>
								</Card>

								{window.location.hostname === 'localhost' && (
									<pre>{JSON.stringify(formik, null, 2)}</pre>
								)}
								{/* </div> */}
							</Grid>
							<Grid item sm={12} md={4}>
								<CreateQuoteSummaryPanel
									options={formik}
									declineQuestions={declineQuestions}
									addressComponent={
										<PolicyPeriodDatePickers
											options={formik}
											selectedStartDate={selectedStartDate}
											selectedEndDate={selectedEndDate}
											setSelectedStartDate={setSelectedStartDate}
											setSelectedEndDate={setSelectedEndDate}
										/>
									}
								>
									{(formik.values.products.length > 0 ||
										formik.values.packages.length > 0) && (
										<>
											<Divider
												sx={{
													mb: '1em',
													borderBottomWidth: '5px',
													borderColor: colours.primaryLight.main,
												}}
											/>
											<Box px='1em' pb='0.5em'>
												<Typography variant='cardSubtitle1'>
													Your Products and Limits
												</Typography>
											</Box>
											<QuoteSummaryProducts
												// quoteCheckbox={quoteCheckbox}
												checkBoxHandler={checkBoxHandler}
												options={formik}
											/>
										</>
									)}
								</CreateQuoteSummaryPanel>
							</Grid>
						</Grid>
					</form>
				)}
			</div>
			<BindModal handleBindQuote={handleBindQuote} options={formik} />
			<ReferQuoteModal
				handleReferral={handleReferral}
				title='Refer client'
				subtitle='Please provide any additional information you consider relevant to Delta underwriters.'
				isFilePick
				referralQuestions={referralQuestions}
			/>
			<ConfirmModal handleExit={handleExit} />
			<UpdateQuoteStatusModal handleConfirm={handleUpdateQuoteStatus} />
			<RemoveProductModal
				handleExit={removeProductClose}
				handleClose={() => setRemoveProductClose(async () => {})}
			/>
		</div>
	);
};

export default CreateQuoteForm;
